import { useTranslation } from "react-i18next";
import { TABLE_HEADERS } from "../constants";
import styles from "./styles.module.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertColor } from "@mui/material";
import CustomizedSnackbars from "../../../components/common/customizedSnackbars";
import { ReactComponent as Completed } from "../../../assets/images/svg/Verified.svg";
import { ICompletedTransferBody } from "../../../setup/api/services/completed-transfers/models";
import classNames from "classnames";
import TooltipWrapper from "../../../components/common/TooltipWrapper";
import { ROUTES } from "../../../setup/constants";
import { getDate } from "../../../setup/util/dateFormatter";

interface IComnpletedTransfersListItem {
  transfer: ICompletedTransferBody;
}

const ComnpletedTransfersListItem = ({
  transfer,
}: IComnpletedTransfersListItem) => {
  const { t } = useTranslation();

  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>("info");
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const onOpenWithdraw = useCallback((id: string): void => {
    window.open(`${ROUTES.WITHDRAWS}/${id}?isOperation=true`, "_blank");
  }, []);

  const onClick = useCallback((id: string): void => {
    window.open(`${ROUTES.CASHOUTS}/${id}?isOperation=true`, "_blank");
  }, []);

  const createRow = (desc: string, value) => {
    return { desc, value };
  };

  const paymentMap = useMemo(() => {
    return {
      BANK: t("lbl_bank_transfer"),
      TABBY: t("lbl_tabby"),
      TAMARA: t("lbl_tamara"),
      NEO: `${t("lbl_neo_amount")} (neo later)`,
      MOYASAR: t("lbl_moyasar"),
      MANUAL: t("lbl_manual_transfer"),
      AUTO: t("lbl_auto_transfer"),
      REFUND: t("lbl_refund"),
      MULTI: t("lbl_multi_transfer"),
    };
  }, [t]);

  const onCopy = async () => {
    try {
      await navigator?.clipboard?.writeText(transfer?.gatewayPaymentId);
      setSnackBarOpen(true);
      setSnackBarColor("success");
      setSnackBarTitle(t("COPIED"));
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBarColor("error");
      setSnackBarTitle(t("COPIED"));
      console.error(error);
    }
  };

  const rows = [
    createRow(
      TABLE_HEADERS.USERNAME,
      <div className={styles["transaction_link"]}>
        <p
          onClick={() => {
            transfer?.manualWithdrawId
              ? onOpenWithdraw(transfer?.manualWithdrawId.toString())
              : onClick(transfer.domainId);
          }}
          dir="rtl">
          {transfer.beneficiaryName || ""}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSACTION_ID,
      <div className={classNames(styles["transaction_payment"])}>
        <TooltipWrapper
          id="payment_id_tooltip"
          content={transfer.gatewayPaymentId || ""}>
          <img src="/assets/images/svg/copy.svg" alt="" onClick={onCopy} />
          <p dir="rtl">{transfer.gatewayPaymentId || ""}</p>
        </TooltipWrapper>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSFER_TIME,
      <div
        className={classNames(
          styles["transaction_cell"],
          styles["transaction_large_cell"]
        )}>
        <p dir="rtl">{getDate(new Date(transfer.updatedAt))}</p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.AMOUNT,
      <div className={styles["transaction_bold"]}>
        <p dir="rtl">
          {transfer.amount.toFixed(2) + " " + t("lbl_saudi_currency")}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSFER_CHANNEL,
      <div className={styles["transaction_cell"]}>
        <p dir="rtl">{paymentMap[transfer.doneThru || ""]}</p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSFER_TYPE,
      <div className={styles["transaction_type"]}>
        <div className={styles["transaction_type_container"]}>
          <p dir="rtl">{paymentMap[transfer.paymentTransactionType || ""]}</p>
          <img src="/assets/images/svg/green_tick.svg" alt="" />
        </div>
        <img src="/assets/images/svg/left_arrow.svg" alt="" />
      </div>
    ),
  ];

  useEffect(() => {
    if (snackBarOpen) {
      setTimeout(() => {
        setSnackBarOpen(false);
        setSnackBarColor("info");
        setSnackBarTitle("");
      }, 5000);
    }
  }, [snackBarOpen]);

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  return (
    <>
      <tr className={styles.table_row}>
        {rows.map((row, index) => (
          <td className={styles.table_value} key={index}>
            {row.value}
          </td>
        ))}
      </tr>
      <div className={styles.snackbar}>{renderSnackBar()}</div>
    </>
  );
};

export default ComnpletedTransfersListItem;
